import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pins as pinsApi, ApiError } from '@/lib/api';
import { usePinsStore } from './store';

/**
 * F5.3 — Add-pin modal launched from DetailsPanel's "📌 Pin this" button.
 *
 * The pin's lat/lon are pre-filled from the comune the user opened, so the
 * form only asks for title (required), optional URL (e.g. Idealista
 * listing), and optional notes. Type is always 'custom' here — Idealista
 * URL parsing was dropped from F5.3 scope (ToS violation).
 */

export function AddPinDialog() {
  const { t } = useTranslation();
  const init = usePinsStore((s) => s.addDialog);
  const close = usePinsStore((s) => s.closeAddDialog);
  const qc = useQueryClient();

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens for a new comune.
  useEffect(() => {
    if (init) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- form reset on prop change; no cascade risk since init is a stable reference from the store
      setTitle(init.comuneName ?? '');
      setUrl('');
      setNotes('');
      setError(null);
    }
  }, [init]);

  const mutation = useMutation({
    mutationFn: () =>
      pinsApi.create({
        type: url.includes('idealista') ? 'idealista' : 'custom',
        title: title.trim(),
        url: url.trim() || null,
        lat: init!.lat,
        lon: init!.lon,
        countryCode: 'IT',
        comuneIstat: init?.comuneIstat ?? null,
        metadata: null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pins'] });
      close();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : t('pins.errorGeneric', { defaultValue: 'Could not save pin' }));
    },
  });

  if (!init) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(t('pins.titleRequired', { defaultValue: 'Title is required' }));
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      {/* Backdrop — separate from dialog so the dialog role itself has no click handler */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={close}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-pin-title"
        className="relative z-10 w-full max-w-md"
      >
      <form
        onSubmit={submit}
        className="rounded-lg bg-[color:var(--color-paper)] p-5 shadow-2xl"
      >
        <h2
          id="add-pin-title"
          className="mb-1 font-serif text-lg font-medium text-[color:var(--color-ink-900)]"
        >
          {t('pins.addTitle', { defaultValue: 'Pin this place' })}
        </h2>
        <p className="mb-4 text-xs text-[color:var(--color-ink-500)]">
          {init.comuneName ?? `${init.lat.toFixed(4)}, ${init.lon.toFixed(4)}`}
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
            {t('pins.fieldTitle', { defaultValue: 'Title' })} *
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            // eslint-disable-next-line jsx-a11y/no-autofocus -- focus trap for accessibility: dialog input should receive focus on open
            autoFocus
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-[color:var(--color-terra-600)] focus:outline-none"
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
            {t('pins.fieldUrl', { defaultValue: 'Listing URL (optional)' })}
          </span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={500}
            placeholder="https://www.idealista.it/..."
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-[color:var(--color-terra-600)] focus:outline-none"
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
            {t('pins.fieldNotes', { defaultValue: 'Notes' })}
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-[color:var(--color-terra-600)] focus:outline-none"
          />
        </label>

        {error && (
          <div role="alert" className="mb-3 rounded bg-red-50 px-3 py-1.5 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !title.trim()}
            className="rounded-md bg-[color:var(--color-terra-600)] px-4 py-1.5 text-sm font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
          >
            {mutation.isPending
              ? t('pins.saving', { defaultValue: 'Saving…' })
              : t('pins.save', { defaultValue: 'Save pin' })}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
